// Backlog row 200 (vault 04-decisions #368): away statuses Break, Lunch,
// Meeting and Training. They are CUSTOM (not routable) statuses in Connect,
// created 22 Oct 2026, and are never billed. Meeting and Training ask the
// agent for a short note. Pure functions so the rules can be tested without a
// browser (scripts/verify-agent-statuses.mjs).

export const STATUS_ORDER = ['Available', 'Break', 'Lunch', 'Meeting', 'Training', 'Offline'];
export const AWAY_STATUSES = new Set(['Break', 'Lunch', 'Meeting', 'Training']);
export const NOTE_REQUIRED = new Set(['Meeting', 'Training']);
export const NOTE_MAX = 120;
const FALLBACK = ['Available', 'Offline'];

// The statuses the agent can pick, from Streams agent.getAgentStates().
// Known statuses come in STATUS_ORDER; any other enabled status Connect
// returns is kept (alphabetical, before Offline) so a status added later is
// not hidden. With nothing readable, fall back to Available and Offline.
export function statusOptions(agentStates) {
  const names = [];
  for (const s of Array.isArray(agentStates) ? agentStates : []) {
    const n = typeof s === 'string' ? s : s?.name;
    if (typeof n === 'string' && n.trim() && !names.includes(n)) names.push(n);
  }
  if (!names.length) return [...FALLBACK];
  const known = STATUS_ORDER.filter(n => names.includes(n) && n !== 'Offline');
  const other = names.filter(n => !STATUS_ORDER.includes(n)).sort((a, b) => a.localeCompare(b));
  const out = [...known, ...other];
  if (names.includes('Offline')) out.push('Offline');
  return out;
}

export function needsNote(name) { return NOTE_REQUIRED.has(name); }
export function isAway(name) { return AWAY_STATUSES.has(name); }

// Returns { ok, note, error }. The note is trimmed, single-line, 1-120 chars.
export function validateNote(text) {
  const note = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!note) return { ok: false, note: '', error: 'Add a short note first.' };
  if (note.length > NOTE_MAX) return { ok: false, note, error: `Keep it to ${NOTE_MAX} characters.` };
  return { ok: true, note, error: null };
}

// Time limits per away status, in minutes (Storm 2026-10-22: "go status
// colours code"; approved 15:32 "approve colour time limits").
export const STATUS_LIMIT_MIN = { Break: 15, Lunch: 30, Meeting: 60, Training: 60 };

// Away statuses change colour with time (Storm 2026-10-22 15:46): neutral,
// amber from 95% of the limit (a warning before it runs out), red from 101%
// (over the limit). Was amber at 100% and red at 150%.
export const AMBER_AT = 0.95;
export const RED_AT = 1.01;
export function awayLevel(status, msInStatus) {
  const limit = STATUS_LIMIT_MIN[status];
  if (!limit) return null;
  const min = (Number(msInStatus) || 0) / 60000;
  if (min >= limit * RED_AT) return 'late';
  if (min >= limit * AMBER_AT) return 'over';
  return 'ok';
}

// Pill colour class for the left rail when not on a contact.
export function pillClassFor(status, msInStatus = 0) {
  if (status === 'Available') return 'available';
  if (isAway(status)) {
    const lvl = awayLevel(status, msInStatus);
    return lvl === 'late' ? 'away away-late' : lvl === 'over' ? 'away away-over' : 'away';
  }
  const s = String(status ?? '').toLowerCase();
  if (s.includes('acw') || s.includes('after call')) return 'acw';
  return 'offline';
}

// Time in the current status for the pill: m:ss under an hour, h:mm:ss after.
export function formatDuration(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// When the status started: Streams' own start time if readable, else the
// moment this console saw the change. Returns epoch ms or null.
export function statusStartMs(agent, fallbackMs = null) {
  try {
    const d = agent?.getStatusDuration?.();
    if (Number.isFinite(d) && d >= 0) return Date.now() - d;
  } catch { /* fall through */ }
  try {
    const t = agent?.getState?.()?.startTimestamp;
    const ms = t instanceof Date ? t.getTime() : Number(t);
    if (Number.isFinite(ms) && ms > 0) return ms;
  } catch { /* fall through */ }
  return fallbackMs;
}

// Row 200 step 3: the body the console posts to /presence when the confirmed
// status changes. The note rides along only if it was given for this status.
export function statusLogBody(status, statusNote) {
  const body = { type: 'status', status };
  if (statusNote && statusNote.status === status && needsNote(status)) body.note = statusNote.note;
  return body;
}

// Post once per confirmed change, only while signed in to the softphone.
export function shouldLogStatus({ connected, status, lastLogged }) {
  return !!connected && typeof status === 'string' && status.length > 0 && status !== lastLogged;
}
